import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import React, { useState } from 'react';

function ErrorMessage({ 
  error, 
  title = '오류가 발생했습니다',
  message,
  onRetry,
  showRetry = true,
  severity = 'error',
  variant = 'standard',
  details = null,
  copyable = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const textToCopy = details || error?.message || message || '오류 정보';
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const getErrorMessage = () => {
    if (message) return message;
    if (error?.message) return error.message;
    if (typeof error === 'string') return error;
    return '알 수 없는 오류가 발생했습니다.';
  };

  const getDetailedError = () => {
    if (details) return details;
    // 개발 환경에서만 상세 디버그 정보 노출
    if (process.env.NODE_ENV === 'development') {
      if (error?.stack) return error.stack;
      if (error?.response?.data) return JSON.stringify(error.response.data, null, 2);
    }
    return null;
  };

  const errorMessage = getErrorMessage();
  const detailedError = getDetailedError();

  return (
    <Alert 
      severity={severity}
      variant={variant}
      action={
        <Box display="flex" alignItems="center" gap={1}>
          {copyable && (
            <IconButton
              size="small"
              onClick={handleCopy}
              title={copied ? '복사됨!' : '오류 정보 복사'}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          )}
          
          {detailedError && (
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
          
          {showRetry && onRetry && (
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              color="inherit"
            >
              다시 시도
            </Button>
          )}
        </Box>
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {errorMessage}
      
      {detailedError && (
        <Collapse in={expanded} timeout="auto">
          <Box 
            sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'rgba(0, 0, 0, 0.04)', 
              borderRadius: 1,
              border: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography variant="caption" color="text.secondary" gutterBottom>
              상세 오류 정보:
            </Typography>
            <Typography 
              variant="body2" 
              component="pre"
              sx={{ 
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
                maxHeight: '200px',
                overflow: 'auto',
              }}
            >
              {detailedError}
            </Typography>
          </Box>
        </Collapse>
      )}
      
      {copied && (
        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
          ✓ 클립보드에 복사되었습니다
        </Typography>
      )}
    </Alert>
  );
}

export default ErrorMessage;